import MedenticAuthEntryPage from "@shared/ui/MedenticAuthEntryPage";
import { ChevronLeftIcon, ChevronRightIcon } from "@medentic/polaris-icons";
import { EntityType, UserPermissionAccess } from "@shared/types/commonTypes";
import { removeLocalStorage, setLocalStorage } from "@shared/lib";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Badge,
  BlockStack,
  Box,
  Button,
  Icon,
  InlineStack,
  ResourceItem,
  ResourceList,
  Text,
} from "@medentic/polaris-react";
import { useSelector } from "react-redux";
import { selectUserPermissions } from "@features/user/model/userSlice";

interface BaseEntity {
  id: string;
  name: string;
  avatar?: {
    initials?: string;
    image?: string;
  };
  website?: string;
  userPermissions?: {
    role: string;
  };
}

interface EntityListProps<T extends BaseEntity> {
  entityType: EntityType;
  title: string;
  addNewLabel: string;
  queryKey: string[];
  rootPath: string;
  lastVisitedKey: string;
  navigateToEntity: (entities?: T[]) => void;
}

function EntityList<T extends BaseEntity>({
  entityType,
  title,
  addNewLabel,
  queryKey,
  rootPath,
  lastVisitedKey,
  navigateToEntity,
}: EntityListProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const entities = queryClient.getQueryData<T[]>(queryKey) || [];
  const permissions = useSelector(selectUserPermissions);
  const hasClinicAccess = permissions.includes(UserPermissionAccess.CLINIC);
  const hasEventAccess = permissions.includes(UserPermissionAccess.EVENT);
  const needBackButton = hasClinicAccess && hasEventAccess;

  // Note: Auto-redirect on initial app load is handled by useInitializeApp.ts
  // When users explicitly navigate to this list, they want to see it (even with 1 entity)
  // so they can add new entities or switch between them

  return (
    <MedenticAuthEntryPage>
      <Box paddingBlockEnd={"500"}>
        <Text as="p" variant="headingLg">
          {title}
        </Text>
      </Box>

      <div className="resource-list-container">
        <Box
          borderColor="border"
          borderWidth="025"
          borderStyle="solid"
          borderRadius="200"
        >
          <ResourceList
            resourceName={{
              singular: entityType,
              plural:
                entityType === EntityType.CLINIC ? "clinics" : "event organizers",
            }}
            items={[
              { id: "new", name: addNewLabel },
              ...(entities || []),
            ]}
            renderItem={(entity: T | { id: string; name: string }) => {
              if (entity.id === "new") {
                return (
                  <ResourceItem
                    verticalAlignment="center"
                    id={entity.id}
                    key={entityType + '_' + entity.id}
                    accessibilityLabel={addNewLabel}
                    onClick={() => navigate(rootPath + "/add")}
                    media={<Avatar size="md" />}
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodyMd" fontWeight="bold" as="p">
                        {entity.name}
                      </Text>
                      <Box>
                        <Icon source={ChevronRightIcon} />
                      </Box>
                    </InlineStack>
                  </ResourceItem>
                );
              }

              const { id, name, avatar, website, userPermissions } =
                entity as T;
              const media = (
                <Avatar
                  size="md"
                  name={name}
                  initials={avatar?.initials}
                  source={avatar?.image}
                />
              );

              return (
                <ResourceItem
                  verticalAlignment="center"
                  id={id}
                  key={entityType + '_' + id}
                  media={media}
                  onClick={() => {
                    removeLocalStorage("__REDIRECT_PATH");
                    setLocalStorage(lastVisitedKey, id);
                    navigateToEntity(entities);
                  }}
                  accessibilityLabel={`View details for ${name}`}
                >
                  <InlineStack
                    align="space-between"
                    wrap={false}
                    blockAlign="center"
                  >
                    <InlineStack align="space-between" gap="100">
                      <BlockStack gap="0">
                        <InlineStack align="start" gap="200">
                          <Text variant="bodyMd" fontWeight="bold" as="h3">
                            {name}
                          </Text>
                          {userPermissions && (
                            <Box>
                              <Badge tone="info">{userPermissions.role}</Badge>
                            </Box>
                          )}
                        </InlineStack>
                        <Box maxWidth="400px">
                          <Text as="p" tone="subdued">
                            {website}
                          </Text>
                        </Box>
                      </BlockStack>
                    </InlineStack>
                    <Box>
                      <Icon source={ChevronRightIcon} />
                    </Box>
                  </InlineStack>
                </ResourceItem>
              );
            }}
          />
        </Box>
      </div>

      <Box paddingBlockEnd={"500"}></Box>
      {needBackButton && (
        <InlineStack gap="400" align="start" blockAlign="center">
          <Button
            onClick={() => {
              navigate("/");
            }}
            icon={ChevronLeftIcon}
            variant="monochromePlain"
          >
            {t("common.back")}
          </Button>
        </InlineStack>
      )}
    </MedenticAuthEntryPage>
  );
}

export default EntityList;
